/** Relative ages and elapsed times, in the spec's voice. */

const MIN = 60_000
const HOUR = 60 * MIN
const DAY = 24 * HOUR

/** "just now" / "14m ago" / "3h ago" / "Yesterday" / "2 days ago" */
export function age(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return '—'
  const d = now - then
  if (d < MIN) return 'just now'
  if (d < HOUR) return `${Math.floor(d / MIN)}m ago`
  if (d < DAY) return `${Math.floor(d / HOUR)}h ago`
  const days = Math.floor(d / DAY)
  if (days === 1) return 'Yesterday'
  return `${days} days ago`
}

/** "14m 02s" / "3h 12m" / "2d 4h" -- a span of milliseconds, written out. */
function span(d: number): string {
  if (d < HOUR) {
    const m = Math.floor(d / MIN)
    const s = Math.floor((d % MIN) / 1000)
    return `${m}m ${String(s).padStart(2, '0')}s`
  }
  if (d < DAY) return `${Math.floor(d / HOUR)}h ${Math.floor((d % HOUR) / MIN)}m`
  return `${Math.floor(d / DAY)}d ${Math.floor((d % DAY) / HOUR)}h`
}

/** "14m 02s" / "3h 12m" / "2d 4h" -- the lane's elapsed column. */
export function elapsed(
  startIso: string | null | undefined,
  endIso?: string | null,
  now: number = Date.now(),
): string {
  if (!startIso) return '—'
  const start = Date.parse(startIso)
  if (Number.isNaN(start)) return '—'
  const end = endIso ? Date.parse(endIso) : now
  return span(Math.max(0, (Number.isNaN(end) ? now : end) - start))
}

/**
 * "in 14m" / "in 3h" / "tomorrow" / "in 2 days" -- a time still to come, which
 * is what a scheduled job's next run is.
 *
 * The mirror of `age()`, and it falls back to it: a due time that has already
 * passed is not "in -20m", it is a job that is late, and "20m ago" is the way
 * this app already writes that. So the status panel's "next due" column says
 * something true either way without the caller testing the clock itself.
 */
export function until(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '—'
  const then = Date.parse(iso)
  if (Number.isNaN(then)) return '—'
  if (then <= now) return age(iso, now)
  const d = then - now
  if (d < MIN) return 'in under a minute'
  if (d < HOUR) return `in ${Math.floor(d / MIN)}m`
  if (d < DAY) return `in ${Math.floor(d / HOUR)}h`
  const days = Math.floor(d / DAY)
  return days === 1 ? 'tomorrow' : `in ${days} days`
}

/**
 * The same writing, from a span already counted in seconds -- the desk's two
 * time columns, which are arithmetic over rows rather than a pair of
 * timestamps. Null is a figure nothing recorded, and reads as '—' rather than
 * as zero.
 */
export function duration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—'
  return span(Math.max(0, seconds) * 1000)
}

/** "14:22" -- the activity feed's mono time column, in the reader's zone. */
export function clock(iso: string): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return '—'
  return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`
}

/** "18 Sep 2026" -- changelog column, agent card dates. */
export function shortDate(iso: string): string {
  const t = new Date(iso)
  if (Number.isNaN(t.getTime())) return '—'
  return t.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}

export function isSameLocalDay(iso: string, now: number = Date.now()): boolean {
  const a = new Date(iso)
  const b = new Date(now)
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}
